import { Button, Form, Segmented, Space } from 'antd';
import { FC } from 'react';

import { AppDatePicker, ButtonSuccess } from '@/common/components';
import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { DomainRegistrarsAccountsAutocomplete, DomainRegistrarsAllAutocomplete } from '@/modules/DomainRegistrars';
import { DomainFormFields, DomainFormInputs } from '@/modules/Domains';

import { DomainsInputMode } from './DomainsCreateFormContainer';
import { DomainsFormDomainsInputWithMods } from './DomainsFormDomainsInputWithMods/DomainsFormDomainsInputWithMods';

interface Props extends AppFormProps<typeof DomainFormInputs, DomainFormFields> {
  domainRegistrarFormId: string | undefined;
  domainsMode: DomainsInputMode | null;
  cannotBeUpdatedCauseStatus?: boolean;
  handleChangeDomainsMode: (mode: DomainsInputMode) => void;
}

export const DomainsFormView: FC<Props> = ({
  formName,
  formType,
  form,
  initialValues,
  isFormLoading,
  formErrors,
  domainRegistrarFormId,
  validationSchema,
  cannotBeUpdatedCauseStatus,
  domainsMode,
  handleChangeDomainsMode,
  handleBack,
  handleFinish,
  handleFinishFailed
}) => {
  return (
    <>
      <Form
        form={ form }
        name={ formName }
        disabled={ isFormLoading }
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={ initialValues }
        autoComplete="off"
        onFinish={ handleFinish }
        onFinishFailed={ handleFinishFailed }
      >
        {
          formType === 'create' && <Form.Item noStyle>
            <Segmented
              block
              onChange={ handleChangeDomainsMode }
              options={ [{ value: 'single', label: 'Один домен' }, { value: 'multiple', label: 'Множина доменів' }] }
            />
          </Form.Item>
        }

        <Form.Item
          required
          label={ formType === 'edit' || domainsMode === 'single' ? 'Назва домену' : 'Назви доменів' }
          hasFeedback
          name={ DomainFormInputs.Names }
          help={ formErrors?.names }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.names) }
          rules={ validationSchema.names }
        >
          <DomainsFormDomainsInputWithMods
            disabled={ cannotBeUpdatedCauseStatus || isFormLoading || formType === 'edit' }
            domainsMode={ domainsMode }
            setFieldValue={ (v) => form.setFieldValue(DomainFormInputs.Names, v) }
            formType={ formType }
            initialNames={ initialValues.names as string[] }
          />
        </Form.Item>

        <Form.Item
          required
          label="Реєстратор"
          name={ DomainFormInputs.DomainRegistrarId }
          hasFeedback
          help={ formErrors?.domainRegistrarId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.domainRegistrarId) }
          rules={ validationSchema.domainRegistrarId }
        >
          <DomainRegistrarsAllAutocomplete disabled={ cannotBeUpdatedCauseStatus || isFormLoading }/>
        </Form.Item>

        <Form.Item
          required
          label="Акаунт реєстратора"
          name={ DomainFormInputs.DomainRegistrarAccountId }
          hasFeedback
          help={ formErrors?.domainRegistrarAccountId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.domainRegistrarAccountId) }
          rules={ validationSchema.domainRegistrarAccountId }
        >
          <DomainRegistrarsAccountsAutocomplete
            allowClear
            domainRegistrarId={ domainRegistrarFormId }
            disabled={ cannotBeUpdatedCauseStatus || isFormLoading }
          />
        </Form.Item>

        <Form.Item
          required
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 23 }}
          label="Дата реєстрації"
          name={ DomainFormInputs.RegistratedAt }
          hasFeedback
          help={ formErrors?.registratedAt }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.registratedAt) }
          rules={ validationSchema.registratedAt }
          style={{ display: 'inline-block', width: 'calc(50%)' }}
        >
          <AppDatePicker allowClear style={{ width: '100%' }}/>
        </Form.Item>

        <Form.Item
          required
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          label="Дата оплати"
          name={ DomainFormInputs.PaidAt }
          hasFeedback
          help={ formErrors?.paidAt }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.paidAt) }
          rules={ validationSchema.paidAt }
          style={{ display: 'inline-block', width: 'calc(50%)' }}
        >
          <AppDatePicker allowClear style={{ width: '100%' }}/>
        </Form.Item>

        <Form.Item wrapperCol={{ span: 24 }}>
          <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button type="default" onClick={ handleBack }>
              Скасувати
            </Button>
            <ButtonSuccess loading={ isFormLoading } type="primary" htmlType="submit">
              { formType === 'edit' ? 'Зберегти' : 'Створити' }
            </ButtonSuccess>
          </Space>
        </Form.Item>
      </Form>
    </>
  );
};
